<template>
    <div class="mt-12">
        <h3 class="pb-4 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">Entity or Trust</h3>

        <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div class="col-span-1">
                <text-field v-model="modelValue.entityName" label="Legal Name of Entity or Trust" :required="true"></text-field>
            </div>

            <div class="relative col-span-1">
                <div class="absolute ml-1 top-1 left-40 group">
                    <svg class="w-4 h-4 text-gray-300 hover:text-gray-400" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"></path></svg>
                    <div class="absolute hidden p-6 mb-1 -ml-4 text-sm font-semibold text-white bg-black rounded-md group-hover:block bottom-full left-full w-80">
                        <p>Your business tax id number goes here. If your LLC or trust does not have a tax id number,  may use an individual social security number. </p>
                    </div>
                </div>
                <text-field v-model="modelValue.taxId" pattern="[0-9]*" inputmode="numeric" v-maska="'#*'" label="Federal Tax ID Number" :required="true"></text-field>
            </div>

            <div class="col-span-1 sm:col-span-2">
                <select-field v-model="modelValue.entityType" label="Entity Type" :options="entityTypes" :required="true"></select-field>
            </div>

            <div class="col-span-1 sm:col-span-2">
                <label class="block text-sm font-medium text-gray-700">Do you have sole signature authority?</label>
                <div class="flex flex-col mt-4 space-y-4 sm:items-center sm:space-x-8 sm:flex-row sm:space-y-0">
                    <radio-field v-model="modelValue.soleSignature" value="Yes">Yes</radio-field>
                    <radio-field v-model="modelValue.soleSignature" value="No">No</radio-field>
                </div>
            </div>

            <div class="col-span-1 space-y-4 sm:col-span-2">
                <div v-if="modelValue.soleSignature != 'Yes'">
                    <label class="block text-sm font-medium text-gray-700">Are there more than two signatures required?</label>
                    <div class="flex flex-col mt-4 space-y-4 sm:items-center sm:space-x-8 sm:flex-row sm:space-y-0">
                        <radio-field v-model="modelValue.moreSignatures" value="Yes">Yes</radio-field>
                        <radio-field v-model="modelValue.moreSignatures" value="No">No</radio-field>
                    </div>
                </div>

                <div v-if="modelValue.moreSignatures == 'Yes'">
                    <div class="mt-8 p-4 text-[#856404] rounded-md bg-[#FFF3CD] border border-[#FFEEBA]">
                        <h3 class="text-lg">Entity with more than 2 signatures required</h3>
                        <p class="text-sm">Mammoth team will be in touch with you via email or phone regarding the signatures required to your subscription agreement. Thank you!</p>
                    </div>
                </div>

                <div>
                    <text-field v-model="modelValue.role" label="Your Title/Role within the Entity or Trust" :required="true"></text-field>
                </div>

                <div>
                    <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                        <div class="col-span-1 sm:col-span-3">
                            <div class="block text-sm font-medium text-gray-700">Entity Address <span class="font-bold text-teal">*</span></div>
                        </div>
                        <div class="col-span-1 sm:col-span-3">
                            <checkbox-field v-model="sameAsAbove">Same as above</checkbox-field>
                        </div>

                        <div class="col-span-1 sm:col-span-3">
                            <address-field v-model="modelValue.address1" @placechanged="onPlaceChanged" label="Address Line 1" :required="true"></address-field>
                        </div>

                        <div class="col-span-1 sm:col-span-3">
                            <text-field v-model="modelValue.address2" label="Address Line 2" :required="false"></text-field>
                        </div>

                        <div class="col-span-1">
                            <text-field v-model="modelValue.city" label="City" :required="true"></text-field>
                        </div>

                        <div class="col-span-1">
                            <template v-if="modelValue.country == 'US'">
                                <select-field v-model="modelValue.state" label="State" label-by="name" track-by="abbreviation" :options="states" :searchable="true" :required="true"></select-field>
                            </template>
                            <template v-else>
                                <text-field v-model="modelValue.state" label="State" :required="true"></text-field>
                            </template>
                        </div>

                        <div class="col-span-1">
                            <text-field v-model="modelValue.zip" v-maska="'#*'" label="Zip Code" :required="true"></text-field>
                        </div>

                        <div class="col-span-1">
                            <select-field v-model="modelValue.country" label="Country" label-by="country" track-by="abbreviation" :options="countries" :searchable="true" :required="true"></select-field>
                        </div>

                        <div class="col-span-1 sm:col-span-3">
                            <label class="block text-sm font-medium text-gray-700">Does anyone, including you, own 25% or more of this Entity/Trust?</label>
                            <div class="flex flex-col mt-4 space-y-4 sm:items-center sm:space-x-8 sm:flex-row sm:space-y-0">
                                <radio-field v-model="modelValue.else25shares" value="Yes">Yes</radio-field>
                                <radio-field v-model="modelValue.else25shares" value="No">No</radio-field>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div v-if="modelValue.soleSignature != 'Yes' && modelValue.else25shares == 'Yes'" class="mt-8">
            <h3 class="pb-4 text-lg font-medium leading-6 text-left no-underline normal-case non-italic">Beneficial Owner</h3>
            <p class="pb-4 text-sm font-medium leading-5">The following information for each individual, if any, who directly or indirectly, through any contract, arrangement, understanding, relationship or otherwise, owns 25 percent or more of the equity interest.</p>
            <div class="mb-5 p-4 text-[#856404] rounded-md bg-[#FFF3CD] border border-[#FFEEBA]">
                <p class="text-sm font-medium">Please provide detailed information for any individual other than yourself who owns 25% or more of the legal entity/trust listed above. We are required by law to collect this information.</p>
            </div>
            <div class="divide-y divide-gray-200">
                <div v-for="(beneficiary, index) in modelValue.beneficiary" :key="index" class="relative">
                    <div class="relative pt-4 pb-10" :class="{
                        'bg-gray-100 rounded-md !p-5 mb-5': beneficiariesNum > 1,
                        'mt-5': beneficiariesNum > 1 && index > 0
                    }">
                        <a href="#" v-show="beneficiariesNum > 1" @click.prevent="removeBeneficiary(index)" class="absolute block -top-2 -left-2"><svg class="w-6 h-6 text-red-500" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"></path></svg></a>

                        <div class="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <div class="col-span-1">
                                <text-field v-model="beneficiary.firstName" label="First Name" :required="true"></text-field>
                            </div>

                            <div class="col-span-1">
                                <text-field v-model="beneficiary.lastName" label="Last Name" :required="true"></text-field>
                            </div>

                            <div class="col-span-1">
                                <text-field v-model="beneficiary.nickname" label="Nickname" :required="false"></text-field>
                            </div>

                            <div class="col-span-1 sm:col-span-3">
                                <text-field v-model="beneficiary.address" label="Address Line (Residential or Business Street Address)" :required="false"></text-field>
                            </div>

                            <div class="col-span-1 sm:col-span-2">
                                <text-field v-model="beneficiary.ssn" v-maska="'###-##-####'" label="If US Person: Social Security Number/Tax ID" :required="false"></text-field>
                            </div>

                            <div class="col-span-1">
                                <text-field v-model="beneficiary.dob" v-maska="'##/##/####'" label="Date of Birth" placeholder="MM/DD/YYYY" :required="false"></text-field>
                            </div>

                            <div class="col-span-1 sm:col-span-3">
                                <text-field v-model="beneficiary.idNumber" label="For Foreign Persons: Passport Number, and Country of Issuance, or ther similar identification number" :required="false"></text-field>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="mt-10" v-if="beneficiariesNum < 4">
                <app-button @click="addBeneficiary" class="!w-full !text-center !border-gray-300 !bg-gray-300 hover:!border-teal hover:!bg-teal">&plus; Add Beneficial Owner</app-button>
            </div>
        </div>
    </div><!-- ENTITY -->
</template>

<script>
import AppButton from '@Components/Button'
import TextField from '@Fields/Text'
import TextareaField from '@Fields/Textarea'
import SelectField from '@Fields/Select'
import CheckboxField from '@Fields/Checkbox'
import RadioField from '@Fields/Radio'
import DateField from '@Fields/Date'
import UploadsField from '@Fields/Uploads'
import AddressField from '@Fields/Address.vue'
import dataCountries from '@Data/countries'
import dataStates from '@Data/us-states'
import PersonalInformationForm from '@/Pages/Intake/Parts/PersonalInformation'
import { computed, watch, ref } from 'vue'

export default {
    components: {
        AppButton,
        TextField,
        TextareaField,
        SelectField,
        CheckboxField,
        RadioField,
        DateField,
        UploadsField,
        AddressField,
        PersonalInformationForm,
    },

    emits: ['update:modelValue', 'replicate'],

    props: ['modelValue', 'series'],

    setup(props, { emit }){
        const sameAsAbove = ref(false)

        watch(() => sameAsAbove.value, (state) => {
            if (state) {
                emit('replicate', 'entity')
            }
        })

        const beneficiariesNum = computed(() => {
            return props.modelValue.beneficiary.length
        })

        function onPlaceChanged(place){
            if (place.city) {
                props.modelValue.city = place.city
            }

            if (place.state) {
                props.modelValue.state = place.state
            }

            if (place.zip) {
                props.modelValue.zip = place.zip
            }

            if (place.country) {
                props.modelValue.country = place.country
            }
        }

        function addBeneficiary(){
            if (beneficiariesNum.value >= 4) {
                return false
            }

            props.modelValue.beneficiary.push({
                firstName: '',
                lastName: '',
                address: '',
                ssn: '',
                dob: '',
                idNumber: ''
            })
        }

        function removeBeneficiary(index){
            props.modelValue.beneficiary.splice(index, 1)
        }

        return {
            sameAsAbove,
            beneficiariesNum,
            onPlaceChanged,
            addBeneficiary,
            removeBeneficiary
        }
    },

    computed: {
        countries(){
            return dataCountries
        },
        states(){
            return dataStates;
        },
        entityTypes(){
            return [
                'Limited Liability Company',
                'Limited Liability Partnership',
                'Revocable Trust',
                'Irrevocable Trust',
                'Professional Corporation',
                'S Corp',
                'Foundation',
                'Endownment'
            ]
        },
    }
}
</script>

<style>

</style>
